import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
  ArrowLeft, Plus, Edit2, Trash2, FileText, Link as LinkIcon, Eye, EyeOff, Inbox, Sparkles, X,
} from 'lucide-react';
import { FORM_TEMPLATES } from '../data/formTemplates';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Admin landing page for the form builder. Lists all forms, supports
 * create / delete / activate-toggle / share-link generation. Editing a form
 * routes into FormEditor.
 *
 * Mirrors the InvoiceTemplatesManager pattern so admin muscle-memory carries
 * over.
 */
export default function FormsManager() {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [templatePicker, setTemplatePicker] = useState(false);
  const [templateBusy, setTemplateBusy] = useState(null); // key of in-flight template

  const authHeader = () => ({
    headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/forms`, authHeader());
      setForms(res.data || []);
    } catch (e) {
      toast.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createForm = async () => {
    const name = window.prompt('Form name (e.g. "Vaccination records", "PAR-Q intake")');
    if (!name || !name.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/forms`, { name: name.trim() }, authHeader());
      toast.success('Form created');
      navigate(`/forms/${res.data.id}`);
    } catch (e) {
      toast.error('Failed to create form');
    } finally {
      setCreating(false);
    }
  };

  const deleteForm = async (form) => {
    if (!window.confirm(`Delete "${form.name}"? Submissions will also be deleted. This can't be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/forms/${form.id}`, authHeader());
      toast.success('Form deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete form');
    }
  };

  const toggleActive = async (form) => {
    try {
      await axios.put(`${API_URL}/forms/${form.id}`, { is_active: !form.is_active }, authHeader());
      load();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  /**
   * Create a form from a pre-seeded template by chaining the existing
   * form-create + question-create endpoints client-side. Each question
   * POST is sequential so the display order matches the template's
   * intent (the backend orders by created_at when display_order is
   * unset). On any failure mid-way the partial form remains — admin can
   * still edit / delete it from the list.
   */
  const createFromTemplate = async (tpl) => {
    setTemplateBusy(tpl.key);
    try {
      const formRes = await axios.post(`${API_URL}/forms`, {
        name: tpl.name,
        description: tpl.description || '',
        intro_text: tpl.intro_text || '',
        thank_you_text: tpl.thank_you_text || '',
      }, authHeader());
      const formId = formRes.data.id;

      for (let i = 0; i < tpl.questions.length; i++) {
        const q = tpl.questions[i];
        await axios.post(`${API_URL}/forms/${formId}/questions`, {
          label: q.label,
          type: q.type,
          is_required: !!q.is_required,
          placeholder: q.placeholder || '',
          help_text: q.help_text || '',
          options: q.options || null,
          display_order: i,
        }, authHeader());
      }

      toast.success(`Created "${tpl.name}"`);
      setTemplatePicker(false);
      navigate(`/forms/${formId}`);
    } catch (e) {
      toast.error('Failed to create form from template');
    } finally {
      setTemplateBusy(null);
    }
  };

  const generateShareLink = async (form) => {
    try {
      const res = await axios.post(`${API_URL}/forms/${form.id}/share-link`, {}, authHeader());
      const url = `${window.location.origin}/forms/fill/${res.data.share_token}`;
      // Copy to clipboard if available, otherwise show in a prompt.
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        toast.success(`Share link copied — expires in ${res.data.expires_in_days} days`);
      } else {
        window.prompt('Copy this share link:', url);
      }
    } catch (e) {
      toast.error('Failed to generate share link');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-5xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              title="Back to admin"
            >
              <ArrowLeft size={24} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
                <FileText className="text-primary-600" size={26} />
                <span>Forms</span>
              </h1>
              <p className="text-sm text-gray-600">
                Build reusable intake, consent, and waiver forms. Share via tokenized link.
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setTemplatePicker(true)}
              className="btn btn-secondary flex items-center space-x-2"
              title="Start from a pre-seeded template (PAR-Q, vaccination, etc.)"
            >
              <Sparkles size={16} />
              <span>From template</span>
            </button>
            <button
              onClick={createForm}
              disabled={creating}
              className="btn btn-primary flex items-center space-x-2"
            >
              <Plus size={16} />
              <span>{creating ? 'Creating…' : 'New form'}</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="card text-center py-12 text-gray-500">Loading…</div>
        ) : forms.length === 0 ? (
          <div className="card text-center py-12 text-gray-500">
            <FileText size={48} className="mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No forms yet.</p>
            <p className="text-sm mt-1">Create your first form to capture client intake or consent.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {forms.map((f) => (
              <div key={f.id} className={`card ${!f.is_active ? 'opacity-60' : ''}`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-bold text-gray-900">{f.name}</h3>
                      {!f.is_active && (
                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">INACTIVE</span>
                      )}
                    </div>
                    {f.description && (
                      <p className="text-sm text-gray-600 mt-1">{f.description}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => toggleActive(f)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                      title={f.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {f.is_active ? <Eye size={18} /> : <EyeOff size={18} />}
                    </button>
                    <button
                      onClick={() => generateShareLink(f)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      title="Copy a one-use share link"
                    >
                      <LinkIcon size={18} />
                    </button>
                    <button
                      onClick={() => navigate(`/forms/${f.id}/submissions`)}
                      className="p-2 text-emerald-600 hover:bg-emerald-50 rounded"
                      title="View submissions"
                    >
                      <Inbox size={18} />
                    </button>
                    <button
                      onClick={() => navigate(`/forms/${f.id}`)}
                      className="p-2 text-primary-600 hover:bg-primary-50 rounded"
                      title="Edit"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => deleteForm(f)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {templatePicker && (
        <TemplatePickerModal
          busyKey={templateBusy}
          onPick={createFromTemplate}
          onClose={() => setTemplatePicker(false)}
        />
      )}
    </div>
  );
}

function TemplatePickerModal({ busyKey, onPick, onClose }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-xl font-bold flex items-center space-x-2">
            <Sparkles className="text-primary-600" size={22} />
            <span>Start from a template</span>
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-gray-600">
            Pick a starting point. The form is created with all questions pre-filled — you can edit, reorder, or delete them after.
          </p>
          {FORM_TEMPLATES.map((tpl) => {
            const isBusy = busyKey === tpl.key;
            const isDisabled = !!busyKey;
            return (
              <button
                key={tpl.key}
                onClick={() => onPick(tpl)}
                disabled={isDisabled}
                className={`w-full text-left p-4 border rounded-lg transition-colors ${
                  isDisabled ? 'border-gray-200 opacity-50 cursor-not-allowed' : 'border-gray-200 hover:border-primary-400 hover:bg-primary-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">{tpl.name}</div>
                    <p className="text-sm text-gray-600 mt-1">{tpl.description}</p>
                    <p className="text-xs text-gray-500 mt-2">{tpl.questions.length} question{tpl.questions.length === 1 ? '' : 's'}</p>
                  </div>
                  {isBusy && (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 ml-3" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
        <div className="p-5 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button onClick={onClose} disabled={!!busyKey} className="btn btn-secondary">Close</button>
        </div>
      </div>
    </div>
  );
}
